<template>
  <div
    :class="[
      'card-wrapper',
      { 'realtime-tracking-page': trackingDataStore.data.type === 'PRIVATE' }
    ]"
  >
    <BmCard>
      <div class="card-content">
        <div class="text-wrapper">
          <h3 class="text-header">
            {{ $t('overview.details.updated.currentStatus') }}
          </h3>
          <p class="text-subheader">
            <span v-if="trackingDataStore.isJobDeliveryStatusPreEta">{{
              $t('overview.details.updatesUnavailable')
            }}</span>
            <span v-else-if="websocketStore.isUpdateAvailable">{{
              $t('overview.details.updateAvailable')
            }}</span>
            <span v-else-if="lastUpdatedInMinutes === 0">
              {{ $t('overview.details.updated.justNow') }}
            </span>
            <span v-else-if="lastUpdatedInMinutes === 1">
              {{ $t('overview.details.updated.oneMinute') }}
            </span>
            <span v-else-if="lastUpdatedInMinutes > 1">
              {{
                $t('overview.details.updated.minutes', lastUpdatedInMinutes, {
                  named: {
                    minutes: lastUpdatedInMinutes
                  }
                })
              }}
            </span>
          </p>
        </div>
        <div
          v-if="!trackingDataStore.isJobDeliveryStatusPreEta"
          :class="[
            {
              loading: isButtonLoading,
              'stop-animations': !isAnimationActive
            },
            buttonAnimationClass
          ]"
          class="button-with-animating-icon"
        >
          <BmButton
            :aria-label="$t('overview.details.updated.refresh')"
            :disabled="isButtonLoading"
            :icon="{
              src: getSeasonalAsset('updateIcon') || UpdateIcon,
              position: 'left'
            }"
            :onClick="onRefreshClick"
            size="small"
            type="primary"
          >
            {{ $t('overview.details.updated.refresh') }}
          </BmButton>
        </div>
      </div>
    </BmCard>
  </div>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { getUtmSource } from '@/router/queryParam.js';
import { useTrackingDataStore } from '@/stores/trackingData.js';
import BmCard from '@/v2/design-system/components/Card/BmCard.vue';
import BmButton from '@/v2/design-system/components/Button/BmButton.vue';
import UpdateIcon from '@/v2/design-system/assets/icons/UpdateIcon.vue';
import { useWebsocketStore } from '@/stores/websocket.js';
import { sendAnalytics } from '@/services/analytics.js';
import { useSeasonalTheme } from '@/v2/composables/useSeasonalTheme.js';
import { useAnimation } from '@/v2/composables/useAnimation.js';

const trackingDataStore = useTrackingDataStore();
const websocketStore = useWebsocketStore();

const { getSeasonalAsset, getSeasonalClass, triggerSeasonalAnimation } =
  useSeasonalTheme();
const { isAnimationActive, startTimedAnimation } = useAnimation();

const $rttI18n = inject('rttI18n');
const $t = inject('t');
const route = useRoute();

const timer = ref(null);
const lastUpdatedInMinutes = ref(0);
const refreshInterval = ref(2000);

const isLoading = computed(() => {
  return trackingDataStore.requestState === 'LOADING';
});

const updateTimestamp = computed(() => {
  return trackingDataStore.data.updateTimestamp;
});

const isTimeoutRunning = ref(undefined);

const isButtonLoading = computed(() => {
  return isTimeoutRunning.value || isLoading.value;
});

const buttonAnimationClass = computed(() => {
  const seasonal = getSeasonalClass('updateButtonAnimation');
  return seasonal?.length ? seasonal : 'rotating';
});

const onRefreshClick = () => {
  const analyticsName =
    trackingDataStore.data.type === 'PRIVATE'
      ? 'tracking_page'
      : 'postcode_page';
  sendAnalytics(
    trackingDataStore.data,
    'clickStats',
    `refresh_button_clicked_via_${analyticsName}`,
    { isV2: true }
  );

  isTimeoutRunning.value = !!setTimeout(() => {
    isTimeoutRunning.value = undefined;
  }, 2000);

  startTimedAnimation();

  trackingDataStore.updateTrackingWithTitleAndSessionUpdate(
    {
      utmSource: getUtmSource(route),
      tenantId: $rttI18n.tenantId,
      trackingId: route.params.trackingId,
      postcode: route.params.postcode
    },
    true,
    $t
  );

  triggerSeasonalAnimation();
};

const setLastUpdated = () => {
  const lastUpdatedInSeconds = (Date.now() - updateTimestamp.value) / 1000;
  lastUpdatedInMinutes.value = Math.floor(lastUpdatedInSeconds / 60, 10);
};

const updateInterval = () => {
  timer.value = setInterval(setLastUpdated, refreshInterval.value);
};

onMounted(() => {
  updateInterval();
  setLastUpdated();
});

onBeforeUnmount(() => {
  clearInterval(timer.value);
});
</script>

<style scoped>
.card-wrapper {
  font-size: var(--body-small-semi-size);

  .card {
    padding: var(--pad-space-sp-14) var(--pad-space-sp-16);
  }
}

@media (width < 480px) {
  .card-wrapper {
    width: 100%;
  }
}

.button-with-animating-icon {
  margin: auto 0;
  white-space: nowrap;
  flex-shrink: 0;

  .button {
    gap: var(--pad-space-sp-8);
    padding: var(--pad-space-sp-6) var(--pad-space-sp-14) var(--pad-space-sp-6)
      var(--pad-space-sp-12);
  }
}

.button-with-animating-icon.loading {
  &.rotating {
    &:deep(button .icon svg) {
      animation: rotate 1s linear infinite;
    }

    &.stop-animations {
      &:deep(button .icon svg) {
        animation: none;
        transform: rotate(45deg);
      }
    }
  }

  &.pulsing {
    &:deep(button .icon svg) {
      animation: pulse 1s linear infinite;
    }

    &.stop-animations {
      &:deep(button .icon svg) {
        animation: none;
        transform: scale(1.2);
      }
    }
  }

  &.shaking {
    &:deep(button .icon svg) {
      animation: easter-egg-wiggle 4s ease-in-out infinite;
    }

    &.reduced-motion {
      &:deep(button .icon svg) {
        animation: none;
        transform: scale(1.2);
      }
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes easter-egg-wiggle {
  0%,
  100% {
    transform: rotate(0deg) translateY(0);
  }
  10% {
    transform: rotate(-14deg) translateY(-0.3px);
  }
  20% {
    transform: rotate(13deg) translateY(0);
  }
  30% {
    transform: rotate(-12deg) translateY(-0.3px);
  }
  40% {
    transform: rotate(11deg) translateY(0);
  }
  50% {
    transform: rotate(-10deg) translateY(-0.3px);
  }
  60% {
    transform: rotate(19deg) translateY(0);
  }
  70% {
    transform: rotate(-8deg) translateY(-0.3px);
  }
  80% {
    transform: rotate(7deg) translateY(0);
  }

  85% {
    transform: rotate(0deg) translateY(0);
  }
}

.card-content {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: var(--pad-space-sp-8);
}

.text-wrapper {
  flex: 1 1 auto;
  overflow-wrap: anywhere;
  min-width: 0;
}

.text-header {
  margin: 0 !important;
  color: var(--neutral-text-icon-pressed) !important;
  font-family: inherit !important;
  font-size: inherit !important;
  font-weight: var(--body-small-semi-weight) !important;
  line-height: var(--line-height-lead-sm) !important;
}

.text-subheader {
  color: var(--neutral-text-icon-label);
  font-family: var(--font-family-body), serif;
  font-size: var(--label-caption-med-size);
  font-weight: var(--label-caption-med-weight);
  line-height: var(--label-caption-med-line-height);
}
</style>
